import React, { FC } from 'react';
import { concatClasses } from '@blink/components/src/utils';
import { ReactComponent as HealthIcon } from '@blink/assets/src/icons/loaderDashWhite.svg';

import { useCollateralHealthCardStyle } from './CollateralHealthCard.style';
import { RiskFactorBar } from 'src/components/RiskFactor/RiskFactorBar';
import { RiskFactorTooltip } from 'src/components/RiskFactor/RiskFactorTooltip';

import { InfoCard, Loader, Skeleton } from '@/shared/ui';
import { formatWithCommas } from '@/shared/utils/helpers';
import { useGetRiskLevel } from '@/shared/hooks/useGetRiskLevel';

type CollateralHealthCardProps = {
    health: number | null;
    marginCallThreshold: string | null;
    liquidationThreshold: string | null;
};

export const CollateralHealthCard: FC<CollateralHealthCardProps> = ({
    health,
    marginCallThreshold,
    liquidationThreshold,
}) => {
    const classes = useCollateralHealthCardStyle();

    const loading = health === null;
    const { isHigh, isWarning, isLow } = useGetRiskLevel({
        health: health!.toString(),
        marginCallThreshold: marginCallThreshold,
        liquidationThreshold: liquidationThreshold,
    });

    let healthClassName = classes.error;

    if (isLow) {
        healthClassName = classes.success;
    } else if (isWarning) {
        healthClassName = classes.warning;
    }

    const formattedHealth = health?.toFixed(8).replace(/\.?0+$/, '');

    const getHealthBadge = () => {
        if (loading) {
            return (
                <div className={classes.flexWrapper}>
                    <Loader className='mr-3' />
                    <div>Waiting</div>
                </div>
            );
        }

        return (
            <div
                className={concatClasses(
                    classes.healthBadge,
                    isLow ? classes.healthBadgeSuccess : '',
                    isWarning ? classes.healthBadgeWarning : '',
                    isHigh ? classes.healthBadgeError : '',
                )}
            >
                {isLow && (
                    <>
                        <HealthIcon />
                        <div>Safe</div>
                    </>
                )}
                {isWarning && (
                    <>
                        <HealthIcon />
                        <div>Warning</div>
                    </>
                )}
                {isHigh && (
                    <>
                        <HealthIcon />
                        <div>High Risk</div>
                    </>
                )}
            </div>
        );
    };

    const getInfoCardType = () => {
        if (isLow) {
            return InfoCard.types.SUCCESS;
        }

        if (isWarning) {
            return InfoCard.types.ALERT;
        }

        if (isHigh) {
            return InfoCard.types.ERROR;
        }

        return InfoCard.types.INFO;
    };

    const getInfoCardText = () => {
        if (loading) {
            return "Secure your position by providing ample collateral to determine the trade's risk factor";
        }

        return isLow || isWarning
            ? 'If the health factor goes below 1, the liquidation of your collateral might be triggered.'
            : 'Your leverage is high, please manage your risk accordingly.';
    };

    const infoCardType = getInfoCardType();

    const tooltipRiskFactor = (
        <p>
            Ensure you have sufficient collateral to maintain a low-risk factor and avoid
            liquidation. Adding more collateral will reduce your risk and keep your position in a
            safe zone
        </p>
    );

    return (
        <div>
            <div className={classes.healthFactor}>
                <div className={classes.flexWrapper}>
                    <RiskFactorTooltip riskFactor={formattedHealth || ''} />
                    <div className={classes.secondary}>/</div>
                    {loading ? (
                        <Skeleton className={classes.micro} />
                    ) : (
                        <div className={healthClassName}>{formatWithCommas(formattedHealth)}</div>
                    )}
                </div>
                {getHealthBadge()}
            </div>
            <RiskFactorBar
                health={health!}
                marginCallThreshold={marginCallThreshold}
                liquidationThreshold={liquidationThreshold}
            />
            <InfoCard
                type={infoCardType}
                text={getInfoCardText()}
                tooltipText={infoCardType === InfoCard.types.INFO ? tooltipRiskFactor : undefined}
                classes={{ root: classes.infoCard }}
            />
        </div>
    );
};
